import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { getJobById } from "../services/jobsService";
import { uploadResume, addApplication } from "../services/applicationsService";
import { isSupabaseConfigured } from "../supabase/client";
import "./JobApply.css";

const MAX_RESUME_MB = 5;

const emptyForm = {
  fullName: "",
  email: "",
  phone: "",
  city: "",
  matric: "",
  intermediate: "",
  degree: "",
  fieldOfStudy: "",
  institution: "",
  graduationYear: "",
  gpa: "",
  experienceYears: "",
  linkedinUrl: "",
  coverNote: "",
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const JobApply = () => {
  const { jobId } = useParams();
  const location = useLocation();
  const passedJob = location.state?.job;

  const [job, setJob] = useState(passedJob || null);
  const [status, setStatus] = useState(passedJob ? "ready" : "loading");
  const [form, setForm] = useState(emptyForm);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (passedJob) return;
    let cancelled = false;
    (async () => {
      if (!isSupabaseConfigured) { setStatus("notfound"); return; }
      try {
        const j = await getJobById(jobId);
        if (!cancelled) {
          setJob(j);
          setStatus(j && j.active !== false ? "ready" : "notfound");
        }
      } catch (e) {
        if (!cancelled) setStatus("notfound");
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, passedJob]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onResume = (e) => {
    const f = e.target.files[0];
    if (!f) { setResume(null); return; }
    if (f.size > MAX_RESUME_MB * 1024 * 1024) {
      setError(`Resume must be under ${MAX_RESUME_MB} MB.`);
      e.target.value = "";
      return;
    }
    setError("");
    setResume(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!resume) return setError("Please attach your resume.");
    setSubmitting(true);
    try {
      const resumePath = await uploadResume(resume);
      await addApplication({
        ...form,
        jobId: job?.id || null,
        jobTitle: job?.title || "",
        jobDepartment: job?.department || "",
        resumePath,
      });

      // Best-effort admin notification (application is already saved either way).
      try {
        const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
        const templateId = process.env.REACT_APP_EMAILJS_APPLICATION_TEMPLATE_ID;
        const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
        if (serviceId && templateId && publicKey) {
          await emailjs.send(serviceId, templateId, {
            job_title: job?.title || "",
            job_department: job?.department || "",
            applicant_name: form.fullName,
            applicant_email: form.email,
            applicant_phone: form.phone,
            applicant_city: form.city,
            matric: form.matric,
            intermediate: form.intermediate,
            degree: form.degree,
            field_of_study: form.fieldOfStudy,
            institution: form.institution,
            graduation_year: form.graduationYear,
            gpa: form.gpa,
            experience_years: form.experienceYears,
            linkedin_url: form.linkedinUrl,
            cover_note: form.coverNote,
          }, { publicKey });
        }
      } catch (mailErr) {
        /* notification is best-effort; the DB record is the source of truth */
      }

      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  if (status === "loading") {
    return <div className="apply-state"><div className="apply-spinner" /></div>;
  }

  if (status === "notfound" || !job) {
    return (
      <div className="apply-state">
        <h2>Position not available</h2>
        <p>This opening may have been filled or removed.</p>
        <Link to="/careers" className="apply-back-btn">&larr; Back to Careers</Link>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="apply-page">
        <div className="apply-container apply-success">
          <div className="apply-success-mark">✓</div>
          <h1>Application received</h1>
          <p>Thank you{form.fullName.trim() ? `, ${form.fullName.trim().split(" ")[0]}` : ""}. Your application for <strong>{job.title}</strong> has been submitted. Our team will contact you if the role is a match.</p>
          <Link to="/careers" className="apply-primary-btn">Back to Careers</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="apply-page">
      <div className="apply-container">
        <Link to="/careers" className="apply-back">&larr; All Openings</Link>

        <motion.div className="apply-head" initial="hidden" animate="visible" variants={fadeUp}>
          <span className="apply-eyebrow">Application</span>
          <h1>{job.title}</h1>
          <div className="apply-jobmeta">
            {[job.department, job.location, job.type].filter(Boolean).map((m, i) => (
              <span key={i} className="apply-tag">{m}</span>
            ))}
          </div>
        </motion.div>

        {error && <div className="apply-alert">{error}</div>}

        <form className="apply-form" onSubmit={handleSubmit}>
          <div className="apply-section">
            <h3 className="apply-section-title">Personal Details</h3>
            <div className="apply-row">
              <div className="apply-field">
                <label>Full name *</label>
                <input value={form.fullName} onChange={setField("fullName")} required />
              </div>
              <div className="apply-field">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={setField("email")} required />
              </div>
            </div>
            <div className="apply-row">
              <div className="apply-field">
                <label>Phone *</label>
                <input value={form.phone} onChange={setField("phone")} required />
              </div>
              <div className="apply-field">
                <label>City</label>
                <input value={form.city} onChange={setField("city")} />
              </div>
            </div>
          </div>

          <div className="apply-section">
            <h3 className="apply-section-title">Academic Background</h3>
            <div className="apply-row">
              <div className="apply-field">
                <label>Matric / O-Level</label>
                <input value={form.matric} onChange={setField("matric")} placeholder="e.g. 88% / A grade" />
              </div>
              <div className="apply-field">
                <label>Intermediate / A-Level (FSc)</label>
                <input value={form.intermediate} onChange={setField("intermediate")} placeholder="e.g. 85% / A grade" />
              </div>
            </div>
            <div className="apply-row">
              <div className="apply-field">
                <label>Highest degree *</label>
                <input value={form.degree} onChange={setField("degree")} required placeholder="e.g. BSc / MBA" />
              </div>
              <div className="apply-field">
                <label>Field of study</label>
                <input value={form.fieldOfStudy} onChange={setField("fieldOfStudy")} placeholder="e.g. Textile Engineering" />
              </div>
            </div>
            <div className="apply-row">
              <div className="apply-field">
                <label>Institution / University</label>
                <input value={form.institution} onChange={setField("institution")} />
              </div>
              <div className="apply-field">
                <label>GPA / CGPA</label>
                <input value={form.gpa} onChange={setField("gpa")} placeholder="e.g. 3.6 / 4.0" />
              </div>
            </div>
            <div className="apply-row">
              <div className="apply-field">
                <label>Graduation year</label>
                <input value={form.graduationYear} onChange={setField("graduationYear")} placeholder="e.g. 2023" />
              </div>
              <div className="apply-field">
                <label>Years of experience</label>
                <input value={form.experienceYears} onChange={setField("experienceYears")} placeholder="e.g. 3" />
              </div>
            </div>
          </div>

          <div className="apply-section">
            <h3 className="apply-section-title">Resume &amp; Links</h3>
            <div className="apply-field">
              <label>Resume * <span className="apply-hint-inline">(PDF or Word, max {MAX_RESUME_MB} MB)</span></label>
              <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={onResume} />
            </div>
            <div className="apply-field">
              <label>LinkedIn / portfolio URL</label>
              <input value={form.linkedinUrl} onChange={setField("linkedinUrl")} placeholder="https://" />
            </div>
            <div className="apply-field">
              <label>Cover note</label>
              <textarea value={form.coverNote} onChange={setField("coverNote")} rows={5} placeholder="Briefly describe why you are suited to this role." />
            </div>
          </div>

          <button className="apply-primary-btn" type="submit" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default JobApply;
